import {
  css as emotionCss,
  cx,
  keyframes as emotionKeyframes,
} from "@emotion/css";
import isPropValid from "@emotion/is-prop-valid";
import type { CSSObject } from "@emotion/styled";
import emotionStyled from "@emotion/styled";
import type React from "react";
import type { FixThisAnyLater, LegitimateAny } from "@kivra/sdk/types/util";
import type {
  BorderRadiusToken,
  BoxShadowToken,
  ColorToken,
  ColorValue,
  FontToken,
  KeyToken,
  SpacingToken,
} from "../designTokens";
import { keyTokens, valueTokens } from "../designTokens";
import deprecationList from "../designTokens/deprecatedTokens.json" with {
  type: "json",
};

type SpacingPrimitives = 0 | "auto" | "inherit";
type SpacingValue<Name extends string> = Name | `-${Name}` | SpacingPrimitives;
export type SpacingValues<S extends string | number> =
  | S
  | [vertical: S, horizontal: S]
  | [top: S, horizontal: S, bottom: S]
  | [top: S, right: S, bottom: S, left: S];

export interface StyleObject extends
  Omit<
    React.CSSProperties,
    "padding" | "margin" | "borderRadius" | "fontFamily"
  > {
  color?: ColorToken | `!!${ColorValue}` | "inherit";
  backgroundColor?: ColorToken | `!!${ColorValue}` | "transparent";
  boxShadow?: React.CSSProperties["boxShadow"] | BoxShadowToken;
  dropShadow?: FixThisAnyLater;
  gap?: SpacingToken;
  borderColor?: ColorToken;

  // Border radius
  borderRadius?: SpacingValues<SpacingValue<BorderRadiusToken>>;
  borderRadiusTopLeft?: SpacingValue<BorderRadiusToken>;
  borderRadiusTopRight?: SpacingValue<BorderRadiusToken>;
  borderRadiusBottomLeft?: SpacingValue<BorderRadiusToken>;
  borderRadiusBottomRight?: SpacingValue<BorderRadiusToken>;

  // Grid
  rowGap?: SpacingToken;
  columnGap?: SpacingToken;
  gridRowGap?: SpacingToken;
  gridColumnGap?: SpacingToken;

  // Margin
  margin?: SpacingValues<SpacingValue<SpacingToken>>;
  marginTop?: SpacingValue<SpacingToken>;
  marginRight?: SpacingValue<SpacingToken>;
  marginBottom?: SpacingValue<SpacingToken>;
  marginLeft?: SpacingValue<SpacingToken>;

  // Padding
  padding?: SpacingValues<SpacingValue<SpacingToken>>;
  paddingTop?: SpacingValue<SpacingToken>;
  paddingRight?: SpacingValue<SpacingToken>;
  paddingBottom?: SpacingValue<SpacingToken>;
  paddingLeft?: SpacingValue<SpacingToken>;

  fontFamily?: FontToken | "inherit";
}

type NonTokenSelectorObject<T> = {
  [key: `$${string}`]: never;
  [key: string]: T;
};

type SelectorObject =
  | { [key: `$${string}`]: never; [key: string]: KivraCSSObject } // this is the same as NonTokenSelectorObject<KivraCSSObject> but ts complains about circular references if we use it
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
  | {
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    [key in KeyToken & keyof NonTokenSelectorObject<never>]?: KivraCSSObject;
  };

export type KivraCSSObject = SelectorObject & StyleObject;

type CSSKey = keyof CSSObject;
type CSSValue = CSSObject[CSSKey];

type ComponentType = StyleableTag | ReactComponent<LegitimateAny>;

type PropsOf<
  C extends
    | keyof React.JSX.IntrinsicElements
    | React.JSXElementConstructor<LegitimateAny>,
> = React.JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>;

type StyledComponentCreatorFunc<C extends ComponentType> = <
  StyledComponentProps extends object = object,
>(
  ...styleCreators: Array<StyleCreator<StyledComponentProps>>
) => (
  props:
    & StyledComponentProps
    & Omit<PropsOf<C>, "className">
    & {
      as?: StyleableTag;
      className?: string;
    },
) => React.ReactNode;

export type StyleCreator<Props extends object> =
  | ((props: Omit<Props, "theme">) => KivraCSSObject)
  | KivraCSSObject;
type StyleCreatorReturnType<Props extends object> = (props: Props) => CSSObject;

type KivraStyledTags = {
  [Tag in StyleableTag]: StyledComponentCreatorFunc<Tag>;
};

type ReactComponent<P> = (
  props: P & { children?: React.ReactNode | undefined; className: string },
  ref?: LegitimateAny,
) => React.ReactNode;

interface KivraStyled extends KivraStyledTags {
  <C extends StyleableTag>(
    componentTagName: C,
    options?: StyledOptions,
  ): StyledComponentCreatorFunc<C>;

  <C extends ReactComponent<React.ComponentProps<C>>>(
    component: C,
    options?: StyledOptions,
  ): StyledComponentCreatorFunc<C>;
}

type StyleableTag =
  | "a"
  | "article"
  | "aside"
  | "b"
  | "blockquote"
  | "br"
  | "button"
  | "canvas"
  | "caption"
  | "circle"
  | "code"
  | "details"
  | "dialog"
  | "div"
  | "embed"
  | "fieldset"
  | "figcaption"
  | "figure"
  | "footer"
  | "form"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "header"
  | "hr"
  | "i"
  | "img"
  | "input"
  | "label"
  | "legend"
  | "li"
  | "link"
  | "main"
  | "mark"
  | "menu"
  | "menuitem"
  | "meter"
  | "nav"
  | "object"
  | "ol"
  | "option"
  | "p"
  | "picture"
  | "pre"
  | "progress"
  | "q"
  | "section"
  | "select"
  | "small"
  | "span"
  | "style"
  | "sub"
  | "summary"
  | "sup"
  | "table"
  | "tbody"
  | "td"
  | "textarea"
  | "tfoot"
  | "th"
  | "thead"
  | "time"
  | "title"
  | "tr"
  | "u"
  | "ul"
  | "svg"
  | "iframe"
  | "video";

interface StyledOptions {
  /**
   * Betermine if a prop should be forward or not.
   */
  forwardProp?: (prop: string) => boolean;
}

function styled<C extends ComponentType>(
  componentType: C,
  options?: StyledOptions,
): StyledComponentCreatorFunc<C> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function componentCreator<StyledComponentProps extends object = object>(
    ...styleCreators: Array<StyleCreator<StyledComponentProps>>
  ) {
    const shouldForwardProp = options?.forwardProp;
    return emotionStyled(componentType as LegitimateAny, {
      shouldForwardProp,
    })(...styleCreators.map((s) => parseKivraStyles(s)));
  }
  /**
   * We're using `any` here since React19. For some reason, styled components
   * from Emotion (`@emotion/styled`) return `ReactNode | Promise<ReactNode>`,
   * which complicates type inference—especially since we’re not using async
   * components (server components). We could probably use a different type
   * here, but I'm not sure what that would be. Since we're creating a wrapper
   * around Emotion, it doesn't really matter if we use `any` internally as long
   * as the public API is type-safe.
   */
  return componentCreator as LegitimateAny;
}

function parseKivraStyles(styleCreator: KivraCSSObject): CSSObject;
function parseKivraStyles<StyledComponentProps extends object>(
  styleCreator: StyleCreator<StyledComponentProps>,
): StyleCreatorReturnType<StyledComponentProps>;
function parseKivraStyles<StyledComponentProps extends object>(
  styleCreator: StyleCreator<StyledComponentProps> | KivraCSSObject,
): StyleCreatorReturnType<StyledComponentProps> | CSSObject {
  if (typeof styleCreator === "function") {
    return (props: StyledComponentProps) => {
      return parseKivraCSSObject(styleCreator(props));
    };
  } else {
    return parseKivraCSSObject(styleCreator);
  }
}

function parseKivraCSSObject(style: number): CSSValue;
function parseKivraCSSObject(style: string): CSSValue;
function parseKivraCSSObject(style: KivraCSSObject): CSSObject;
function parseKivraCSSObject(
  style: KivraCSSObject | string | number,
): CSSObject | CSSValue {
  if (typeof style === "number") {
    return style;
  } else if (typeof style === "string") {
    return replaceValueToken(style);
  } else if (Array.isArray(style)) {
    return replaceValueToken(style.join(" "));
  }
  return Object.entries(style).reduce((obj, [key, value]) => {
    if (![undefined, null].includes(value)) {
      return {
        ...obj,
        [replaceKeyToken(key)]: parseKivraCSSObject(value),
      };
    }
    return obj;
  }, {});
}

const replaceKeyToken = (key: string): CSSKey =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  tokenGuard(key, keyTokens[key as KeyToken] ?? key);

const replaceValueToken = (value: string): CSSValue =>
  tokenGuard(
    value,
    value.includes("$")
      ? replaceTokensInString(value.replace(/!!\$/g, "$"), valueTokens)
      : value,
  );

export const replaceTokensInString = (
  text: string,
  tokens: Record<string, string>,
): string =>
  tokens[text] ??
    Object.entries(tokens).reduce((result, [key, value]) => {
      // regex: match key that is not immediately followed by 0-9, a-z, A-Z, or -
      const regexp = new RegExp(`\\${key}(?![0-9a-zA-Z-])`, "g");
      return result.replace(regexp, value);
    }, text);

const tokenGuard = (original: string, replaced: string): string => {
  if (
    window.location.hostname.startsWith("sandbox") ||
    window.location.hostname === "localhost"
  ) {
    checkDeprecationList(original);
  }
  if (replaced.includes("$")) {
    console.error(`Could not substitute design token in "${original}"`);
  }
  return replaced;
};

const checkDeprecationList = (original: string): void => {
  deprecationList.forEach(({ token, msg }) => {
    if (original.includes(token)) {
      console.warn(`Deprecated token: ${token}\n${msg}`);
    }
  });
};

const wrapInStyledLayer = (s: LegitimateAny): LegitimateAny => {
  if (typeof s === "function") {
    return (props: LegitimateAny) => ({ "@layer styled": s(props) });
  }
  return { "@layer styled": s };
};

const kivraStyled: KivraStyled = new Proxy(styled, {
  get(target, key: StyleableTag) {
    return (...styleCreators: LegitimateAny[]) =>
      target(key)(...styleCreators.map(wrapInStyledLayer));
  },
  apply(target, _thisArg, args: LegitimateAny[]) {
    const creatorFn = target(args[0], args[1]);
    return (...styleCreators: LegitimateAny[]) =>
      creatorFn(...styleCreators.map(wrapInStyledLayer));
  },
}) as LegitimateAny;

export { cx, kivraStyled as styled, parseKivraCSSObject, parseKivraStyles };

export const css = (...style: KivraCSSObject[]): string =>
  emotionCss(
    ...style.map((s) => ({ "@layer overrides": parseKivraStyles(s) })),
  );

export const keyframes = (...style: KivraCSSObject[]): string =>
  emotionKeyframes(...style.map((s) => parseKivraStyles(s)));

/**
 * Functionally all this does is to return the argument as any.
 * Use this when you need to add some styling that is not allowed by our typechecking,
 * but still valid. Use this function instead of casting to any directly so that we can
 * track the occurrance of these exceptions easier.
 */
export const StyleException = (style: LegitimateAny): LegitimateAny => style;

export { isPropValid };
